import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import performanceApiService from '../services/performanceApi';

// Initial state
const initialState = {
  data: {
    performance: null,
    health: null,
    resources: null,
  },
  loading: {
    performance: false,
    health: false,
    resources: false,
    all: false,
  },
  errors: {
    performance: null,
    health: null,
    resources: null,
  },
  lastUpdated: null,
  refreshInterval: 5000, // 5 seconds default
  isAutoRefresh: true,
  settings: {
    refreshInterval: 5000,
    showCharts: true,
    showAlerts: true,
    alertThresholds: {
      eventLoopLatency: 100,
      memoryUsage: 80,
      cpuLoad: 5.0,
    },
  },
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_DATA: 'SET_DATA',
  SET_ERROR: 'SET_ERROR',
  SET_ALL_DATA: 'SET_ALL_DATA',
  SET_REFRESH_INTERVAL: 'SET_REFRESH_INTERVAL',
  SET_AUTO_REFRESH: 'SET_AUTO_REFRESH',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  RESET_STATE: 'RESET_STATE',
};

// Reducer function
const performanceReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.type]: action.payload.loading,
        },
      };

    case ACTION_TYPES.SET_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.type]: action.payload.data,
        },
        loading: {
          ...state.loading,
          [action.payload.type]: false,
        },
        errors: {
          ...state.errors,
          [action.payload.type]: null,
        },
        lastUpdated: new Date().toISOString(),
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.type]: false,
        },
        errors: {
          ...state.errors,
          [action.payload.type]: action.payload.error,
        },
      };

    case ACTION_TYPES.SET_ALL_DATA:
      return {
        ...state,
        data: action.payload.data,
        loading: {
          performance: false,
          health: false,
          resources: false,
          all: false,
        },
        errors: action.payload.errors,
        lastUpdated: new Date().toISOString(),
      };

    case ACTION_TYPES.SET_REFRESH_INTERVAL:
      return {
        ...state,
        refreshInterval: action.payload,
        settings: {
          ...state.settings,
          refreshInterval: action.payload,
        },
      };

    case ACTION_TYPES.SET_AUTO_REFRESH:
      return {
        ...state,
        isAutoRefresh: action.payload,
      };

    case ACTION_TYPES.UPDATE_SETTINGS:
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload,
        },
      };

    case ACTION_TYPES.CLEAR_ERRORS:
      return {
        ...state,
        errors: {
          performance: null,
          health: null,
          resources: null,
        },
      };

    case ACTION_TYPES.RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

// Context creation
const PerformanceContext = createContext();

// Provider component
export const PerformanceProvider = ({ children }) => {
  const [state, dispatch] = useReducer(performanceReducer, initialState);

  // Action creators
  const setLoading = useCallback((type, loading) => {
    dispatch({
      type: ACTION_TYPES.SET_LOADING,
      payload: { type, loading },
    });
  }, []);

  const setData = useCallback((type, data) => {
    dispatch({
      type: ACTION_TYPES.SET_DATA,
      payload: { type, data },
    });
  }, []);

  const setError = useCallback((type, error) => {
    dispatch({
      type: ACTION_TYPES.SET_ERROR,
      payload: { type, error },
    });
  }, []);

  const setAllData = useCallback((data, errors) => {
    dispatch({
      type: ACTION_TYPES.SET_ALL_DATA,
      payload: { data, errors },
    });
  }, []);

  const setRefreshInterval = useCallback((interval) => {
    dispatch({
      type: ACTION_TYPES.SET_REFRESH_INTERVAL,
      payload: interval,
    });
  }, []);

  const setAutoRefresh = useCallback((enabled) => {
    dispatch({
      type: ACTION_TYPES.SET_AUTO_REFRESH,
      payload: enabled,
    });
  }, []);

  const updateSettings = useCallback((settings) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_SETTINGS,
      payload: settings,
    });
  }, []);

  const clearErrors = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.CLEAR_ERRORS,
    });
  }, []);

  const resetState = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.RESET_STATE,
    });
  }, []);

  // API call functions
  const fetchPerformanceMetrics = useCallback(async () => {
    setLoading('performance', true);
    try {
      const data = await performanceApiService.getPerformanceMetrics();
      setData('performance', data);
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      setError('performance', error);
    }
  }, [setLoading, setData, setError]);

  const fetchHealthStatus = useCallback(async () => {
    setLoading('health', true);
    try {
      const data = await performanceApiService.getHealthStatus();
      setData('health', data);
    } catch (error) {
      console.error('Error fetching health status:', error);
      setError('health', error);
    }
  }, [setLoading, setData, setError]);

  const fetchResourceUsage = useCallback(async () => {
    setLoading('resources', true);
    try {
      const data = await performanceApiService.getResourceUsage();
      setData('resources', data);
    } catch (error) {
      console.error('Error fetching resource usage:', error);
      setError('resources', error);
    }
  }, [setLoading, setData, setError]);

  const fetchAllData = useCallback(async () => {
    setLoading('all', true);
    try {
      const result = await performanceApiService.getAllPerformanceData();
      setAllData(result.data, result.errors);
    } catch (error) {
      console.error('Error fetching all performance data:', error);
      setError('all', error);
    }
  }, [setLoading, setAllData, setError]);

  // Auto-refresh effect
  useEffect(() => {
    if (!state.isAutoRefresh) return;

    const interval = setInterval(() => {
      fetchAllData();
    }, state.refreshInterval);

    return () => clearInterval(interval);
  }, [state.isAutoRefresh, state.refreshInterval, fetchAllData]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Computed values
  const isHealthy = useCallback(() => {
    if (!state.data || !state.data.health) return false;
    return performanceApiService.isHealthy(state.data.health);
  }, [state.data?.health]);

  const getPerformanceStatus = useCallback(() => {
    if (!state.data || !state.data.performance) return 'unknown';
    return performanceApiService.getPerformanceStatus(state.data.performance);
  }, [state.data?.performance]);

  const hasErrors = useCallback(() => {
    return Object.values(state.errors).some(error => error !== null);
  }, [state.errors]);

  const isLoading = useCallback(() => {
    return Object.values(state.loading).some(loading => loading === true);
  }, [state.loading]);

  const getAlertCount = useCallback(() => {
    let count = 0;
    
    if (!state.data) return count;
    
    const { performance, health } = state.data;

    if (performance) {
      if (performance.eventLoopLatency?.current > state.settings.alertThresholds.eventLoopLatency) count++;
      if (performance.memory?.processHeap?.percentage > state.settings.alertThresholds.memoryUsage) count++;
      if (performance.cpu?.usage > state.settings.alertThresholds.cpuLoad * 10) count++; // Convert to percentage
    }

    if (health && !isHealthy()) count++;

    return count;
  }, [state.data, state.settings.alertThresholds, isHealthy]);

  // Context value
  const contextValue = {
    // State
    ...state,
    
    // Actions
    fetchPerformanceMetrics,
    fetchHealthStatus,
    fetchResourceUsage,
    fetchAllData,
    setRefreshInterval,
    setAutoRefresh,
    updateSettings,
    clearErrors,
    resetState,
    
    // Computed values
    isHealthy: isHealthy(),
    performanceStatus: getPerformanceStatus(),
    hasErrors: hasErrors(),
    isLoading: isLoading(),
    alertCount: getAlertCount(),
  };

  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
    </PerformanceContext.Provider>
  );
};

// Custom hook to use performance context
export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};

// Export action types for external use
export { ACTION_TYPES };

export default PerformanceContext;
